import { useEffect, useState } from 'react'

import { TreeFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { normalizeNodeType } from 'modules/job_catalog/lib/nodesUtils'
import {
  HierarchyLink,
  JobCatalogNodeData,
} from 'modules/job_catalog/pages/JobCatalogTreePage/types'

interface SerializedTreeNode {
  node_uuid: string
  name: string
  type: string
  children?: SerializedTreeNode[]
}

const useFlattenedTree = (
  treeData: TreeFragment,
  expandFn: (ids: string[]) => void
): { hierarchy: HierarchyLink[]; nodeData: JobCatalogNodeData[] } => {
  const [hierarchy, setHierarchy] = useState<HierarchyLink[]>([])
  const [nodeData, setNodeData] = useState<JobCatalogNodeData[]>([])

  useEffect(() => {
    if (!treeData?.serializedTree) return
    const rawTree = JSON.parse(treeData.serializedTree || '{}')
    const flattenedHierarchy: HierarchyLink[] = []
    const flattenedNodeData: JobCatalogNodeData[] = []

    const rootId = rawTree.node_uuid
    const rootNode: JobCatalogNodeData = {
      id: rootId,
      name: rawTree.name || i18n.t('sidebar.categories.company'),
      type: 'company',
      parentId: null,
      children: [],
    }

    flattenedHierarchy.push({ id: rootNode.id, parentId: null, type: rootNode.type })
    flattenedNodeData.push(rootNode)

    const processNode = (node: SerializedTreeNode, parentUuid: string | null) => {
      const nodeId = node.node_uuid
      const parentId = parentUuid || rawTree.node_uuid
      const nodeType = normalizeNodeType(node.type)

      flattenedHierarchy.push({ id: nodeId, parentId, type: nodeType })

      const newNode: JobCatalogNodeData = {
        id: nodeId,
        name: node.name,
        type: nodeType,
        parentId,
        children: [],
      }

      flattenedNodeData.push(newNode)

      node.children?.forEach((child) => processNode(child, node.node_uuid))
    }

    rawTree.children?.forEach((child: SerializedTreeNode) => processNode(child, rawTree.node_uuid))
    setHierarchy(flattenedHierarchy)
    setNodeData(flattenedNodeData)
  }, [treeData, expandFn])

  return { hierarchy, nodeData }
}

export default useFlattenedTree
